import { Draft } from "immer";
import { Need, NeedDefinition } from "../data/need";

export type FixedField = "fatPercent" | "carbsPercent" | "proteinPercent";

export type PercentField = "fatPercent" | "carbsPercent" | "proteinPercent";

const KCAL_PER_GRAM = { fat: 9, carbs: 4, protein: 4 } as const;

export function getPercentFromAbsolute(
  n: Pick<Need, "kcal">,
  factor: number,
  absolute: number,
) {
  if (n.kcal <= 0) return 0;
  return ((absolute * factor) / n.kcal) * 100;
}

export function getAbsoluteFromPercent(
  n: Pick<Need, "kcal">,
  factor: number,
  percent: number,
) {
  return (n.kcal * percent) / factor / 100;
}

export function needToNeedDefinition(n: Need): NeedDefinition {
  return {
    ...n,
    fatPercent: getPercentFromAbsolute(n, KCAL_PER_GRAM.fat, n.fat),
    carbsPercent: getPercentFromAbsolute(n, KCAL_PER_GRAM.carbs, n.carbs),
    proteinPercent: getPercentFromAbsolute(n, KCAL_PER_GRAM.protein, n.protein),
  };
}

export function applyPercentChange<T extends NeedDefinition>(
  draft: Draft<T>,
  changed: PercentField,
  value: number,
  fixedField: FixedField,
) {
  if (changed === fixedField) return;
  const otherNonAuto = (
    ["fatPercent", "carbsPercent", "proteinPercent"] as PercentField[]
  ).find((f) => f !== changed && f !== fixedField);
  const otherValue = otherNonAuto ? draft[otherNonAuto] : 0;
  const clamped = Math.max(0, Math.min(100 - otherValue, value));

  draft[changed] = clamped as T[typeof changed];
  draft[fixedField] = (100 - clamped - otherValue) as T[FixedField];

  syncAbsoluteFromPercent(draft);
}

export function syncAbsoluteFromPercent<T extends NeedDefinition>(
  draft: Draft<T>,
) {
  draft.fat = getAbsoluteFromPercent(draft, KCAL_PER_GRAM.fat, draft.fatPercent);
  draft.carbs = getAbsoluteFromPercent(
    draft,
    KCAL_PER_GRAM.carbs,
    draft.carbsPercent,
  );
  draft.protein = getAbsoluteFromPercent(
    draft,
    KCAL_PER_GRAM.protein,
    draft.proteinPercent,
  );
}

export function applyAbsoluteChange<T extends NeedDefinition>(
  draft: Draft<T>,
  changed: "fat" | "carbs" | "protein",
  value: number,
  fixedField: FixedField,
) {
  const factor = KCAL_PER_GRAM[changed];
  const percentField = (changed + "Percent") as PercentField;
  if (percentField === fixedField) return;
  applyPercentChange(
    draft,
    percentField,
    getPercentFromAbsolute(draft, factor, value),
    fixedField,
  );
}
